'use client';

import {
  type IconIndex,
  loadIconIndex,
} from '@woolworthslimited/core-assets/dist/icon-index';
import spriteConfig from '@woolworthslimited/core-assets/dist/icon-sprite.config.json';
import { Suspense, useEffect, useState } from 'react';

import { useCoreContext } from '../context/context';

import IconSprite from './icon-sprite';

type SpriteConfig = {
  [key: string]: {
    direct?: { name: string; iconPath: string }[];
    sprite?: {
      spriteName: string;
      spriteIcons: { name: string; iconPath: string }[];
    }[];
  };
};

function toPascalCase(text: string) {
  return text.replace(/(^\w|-\w)/g, clearAndUpper);
}

function clearAndUpper(text: string) {
  return text.replace(/-/, '').toUpperCase();
}

function getSpriteName(brand: string, iconName: string) {
  const spriteData: SpriteConfig = spriteConfig;
  const brandSpriteConfig = spriteData[brand].sprite;

  if (Array.isArray(brandSpriteConfig) && brandSpriteConfig.length > 0) {
    for (const sprite of brandSpriteConfig) {
      if (sprite.spriteIcons.some((icon) => icon.name === iconName)) {
        return sprite.spriteName;
      }
    }
  }

  return undefined;
}

function IconLazy(props: {
  name: string;
  ariaLabel: string | undefined;
  ariaHidden: boolean | undefined;
  className: string;
}) {
  const [isClient, setIsClient] = useState(false);
  const [brandIcons, setBrandIcons] = useState<IconIndex | null>(null);
  const coreContext = useCoreContext();
  const brand = coreContext?.brand || 'woolworths';

  const fallbackIcon = (
    <div data-icon-fallback-name={props.name} className={props.className} />
  );

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    let isActive = true;

    setBrandIcons(null);
    loadIconIndex(brand)
      .then((resolvedIcons) => {
        if (isActive) {
          setBrandIcons(resolvedIcons);
        }
      })
      .catch(() => {
        if (isActive) {
          setBrandIcons(null);
        }
      });

    return () => {
      isActive = false;
    };
  }, [brand]);

  if (!isClient || !brandIcons) {
    return fallbackIcon;
  }

  let Icon;
  const iconIndex = brandIcons;
  let spriteName: string | undefined;

  try {
    const pascalName = toPascalCase(props.name.replace(/^(icon-)/, ''));

    // check if pascalName exist in sprite config
    spriteName = getSpriteName(brand, pascalName);

    Icon = spriteName
      ? iconIndex?.[toPascalCase(`sprite-${spriteName}`)]
      : iconIndex?.[pascalName];
    // eslint-disable-next-line no-empty
  } catch {}

  if (!Icon) {
    return fallbackIcon;
  }

  return (
    <Suspense fallback={fallbackIcon}>
      {!spriteName && (
        <Icon
          aria-label={props.ariaLabel}
          aria-hidden={props.ariaHidden}
          data-icon-name={props.name}
          {...{ className: props.className }}
        />
      )}
      {spriteName && (
        <>
          <IconSprite spriteId={`sprite-${spriteName}`} content={<Icon />} />
          <svg
            aria-label={props.ariaLabel}
            aria-hidden={props.ariaHidden}
            data-icon-name={props.name}
            {...{ className: props.className }}
          >
            <use xlinkHref={`#core-${props.name}`}></use>
          </svg>
        </>
      )}
    </Suspense>
  );
}

export default IconLazy;
